import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  changePassword,
  getMyInvestmentMeta,
  updateMyInfo,
  upsertMyInvestmentMeta,
} from '../../lib/api/user-api';
import './MyPage.css';

const TAB_KEYS = ['profile', 'password', 'investment'];

function MyPage() {
  const { user, token, refreshUserInfo } = useAuth();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);

  const [profileForm, setProfileForm] = useState({
    userName: '',
    phone: '',
    birthDate: '',
    gender: '',
    password: '',
  });
  const [profileError, setProfileError] = useState('');

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: '',
  });
  const [passwordError, setPasswordError] = useState('');

  const [investmentForm, setInvestmentForm] = useState({
    investmentAmount: '',
    houseCount: '',
    isFirstHomeBuyer: false,
    apply: true,
  });
  const [investmentError, setInvestmentError] = useState('');
  const [investmentLoading, setInvestmentLoading] = useState(false);
  const [investmentList, setInvestmentList] = useState([]);

  const activeInvestment = useMemo(
    () => investmentList.find((row) => String(row?.applyYn || '').toUpperCase() === 'Y') || null,
    [investmentList]
  );

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const tab = query.get('tab');
    if (tab && TAB_KEYS.includes(tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  useEffect(() => {
    if (!user) return;
    setProfileForm({
      userName: user.userName || '',
      phone: user.phone || '',
      birthDate: user.birthDate || '',
      gender: user.gender || '',
      password: '',
    });
  }, [user]);

  useEffect(() => {
    if (!token) return;
    let alive = true;

    const loadInvestmentMeta = async () => {
      try {
        const response = await getMyInvestmentMeta(token);
        if (!alive) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setInvestmentList(rows);
        const active = rows.find((row) => String(row?.applyYn || '').toUpperCase() === 'Y') || rows[0];
        if (active) {
          const houseCount = Number(active?.houseCount || 0);
          setInvestmentForm({
            investmentAmount: String(active?.investmentAmount ?? ''),
            houseCount: String(houseCount),
            isFirstHomeBuyer: houseCount === 1 ? Boolean(active?.isFirstHomeBuyer) : false,
            apply: String(active?.applyYn || '').toUpperCase() === 'Y',
          });
        }
      } catch (error) {
        if (!alive) return;
        setInvestmentError(error?.message || '투자정보를 불러오지 못했습니다.');
      }
    };

    loadInvestmentMeta();
    return () => {
      alive = false;
    };
  }, [token]);

  const handleProfileSubmit = async (event) => {
    event.preventDefault();
    if (!profileForm.password) {
      setProfileError('현재 비밀번호를 입력하세요.');
      return;
    }

    setLoading(true);
    setProfileError('');
    try {
      const response = await updateMyInfo(token, {
        password: profileForm.password,
        userName: profileForm.userName,
        phone: profileForm.phone || null,
        birthDate: profileForm.birthDate || null,
        gender: profileForm.gender || null,
      });

      if (response?.success) {
        await refreshUserInfo();
        alert('회원정보가 수정되었습니다.');
        setProfileForm((prev) => ({ ...prev, password: '' }));
      } else {
        setProfileError(response?.message || '정보 수정에 실패했습니다.');
      }
    } catch (error) {
      setProfileError(error?.message || '정보 수정 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    if (passwordForm.newPassword !== passwordForm.newPasswordConfirm) {
      setPasswordError('새 비밀번호가 일치하지 않습니다.');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('새 비밀번호는 6자 이상이어야 합니다.');
      return;
    }

    setLoading(true);
    setPasswordError('');
    try {
      const response = await changePassword(token, passwordForm.currentPassword, passwordForm.newPassword);
      if (response?.success) {
        alert('비밀번호가 변경되었습니다.');
        setPasswordForm({
          currentPassword: '',
          newPassword: '',
          newPasswordConfirm: '',
        });
      } else {
        setPasswordError(response?.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      setPasswordError(error?.message || '비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleInvestmentSubmit = async (event) => {
    event.preventDefault();
    const investmentAmount = Number(investmentForm.investmentAmount);
    const houseCount = Number(investmentForm.houseCount);

    if (!Number.isFinite(investmentAmount) || investmentAmount < 0) {
      setInvestmentError('투자금(만원)은 0 이상 숫자로 입력하세요.');
      return;
    }
    if (!Number.isFinite(houseCount) || houseCount < 0) {
      setInvestmentError('주택수는 0 이상 숫자로 입력하세요.');
      return;
    }

    setInvestmentLoading(true);
    setInvestmentError('');
    try {
      const response = await upsertMyInvestmentMeta(token, {
        investmentAmount,
        houseCount,
        isFirstHomeBuyer: houseCount === 1 && investmentForm.isFirstHomeBuyer,
        apply: Boolean(investmentForm.apply),
      });

      if (!response?.success) {
        setInvestmentError(response?.message || '투자정보 저장에 실패했습니다.');
        return;
      }

      const listResponse = await getMyInvestmentMeta(token);
      const rows = Array.isArray(listResponse?.data) ? listResponse.data : [];
      setInvestmentList(rows);
      await refreshUserInfo();
      alert('투자정보가 저장되었습니다.');
    } catch (error) {
      setInvestmentError(error?.message || '투자정보 저장 중 오류가 발생했습니다.');
    } finally {
      setInvestmentLoading(false);
    }
  };

  if (!user) return <div className="mypage-container">로딩 중...</div>;

  const membership = user.membership || null;

  return (
    <div className="mypage-container">
      <div className="mypage-layout">
        <aside className="mypage-sidebar">
          <div className="user-profile-card">
            <h2 className="user-name-display">{user.userName || user.userId}</h2>
            <p className="user-email-display">{user.email}</p>
            <div className="user-stats">
              <div className="stat-item">
                <span className="stat-label">회원 유형</span>
                <span className="stat-value">{user.userType}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">가입일</span>
                <span className="stat-value">{new Date(user.createdAt).toLocaleDateString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">투자정보</span>
                <span className="stat-value">{activeInvestment ? '설정 완료' : '미설정'}</span>
              </div>
              {membership?.membershipType && (
                <div className="stat-item">
                  <span className="stat-label">멤버십</span>
                  <span className="stat-value">{membership.membershipType}</span>
                </div>
              )}
            </div>
          </div>
        </aside>

        <div className="mypage-main">
          <div className="mypage-tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              회원정보
            </button>
            <button
              className={`tab ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              비밀번호 변경
            </button>
            <button
              className={`tab ${activeTab === 'investment' ? 'active' : ''}`}
              onClick={() => setActiveTab('investment')}
            >
              투자정보
            </button>
          </div>

          <div className="mypage-content">
            {activeTab === 'profile' && (
              <section>
                <h2>회원정보 수정</h2>
                {profileError && <div className="error-message">{profileError}</div>}
                <form className="profile-form" onSubmit={handleProfileSubmit}>
                  <div className="form-group">
                    <label>아이디</label>
                    <input type="text" value={user.userId} disabled />
                  </div>
                  <div className="form-group">
                    <label>이메일</label>
                    <input type="email" value={user.email} disabled />
                  </div>
                  <div className="form-group">
                    <label htmlFor="userName">이름</label>
                    <input id="userName" name="userName" value={profileForm.userName} onChange={(e) => setProfileForm((prev) => ({ ...prev, userName: e.target.value }))} disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">전화번호</label>
                    <input id="phone" name="phone" value={profileForm.phone} onChange={(e) => setProfileForm((prev) => ({ ...prev, phone: e.target.value }))} disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="birthDate">생년월일</label>
                    <input type="date" id="birthDate" name="birthDate" value={profileForm.birthDate} onChange={(e) => setProfileForm((prev) => ({ ...prev, birthDate: e.target.value }))} disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="gender">성별</label>
                    <select id="gender" name="gender" value={profileForm.gender} onChange={(e) => setProfileForm((prev) => ({ ...prev, gender: e.target.value }))} disabled={loading}>
                      <option value="">선택 안함</option>
                      <option value="MALE">남성</option>
                      <option value="FEMALE">여성</option>
                      <option value="OTHER">기타</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">현재 비밀번호 *</label>
                    <input type="password" id="password" name="password" value={profileForm.password} onChange={(e) => setProfileForm((prev) => ({ ...prev, password: e.target.value }))} disabled={loading} />
                  </div>
                  <button type="submit" className="submit-button" disabled={loading}>{loading ? '수정 중...' : '회원정보 저장'}</button>
                </form>
              </section>
            )}

            {activeTab === 'password' && (
              <section>
                <h2>비밀번호 변경</h2>
                {passwordError && <div className="error-message">{passwordError}</div>}
                <form className="password-form" onSubmit={handlePasswordSubmit}>
                  <div className="form-group">
                    <label htmlFor="currentPassword">현재 비밀번호 *</label>
                    <input type="password" id="currentPassword" value={passwordForm.currentPassword} onChange={(e) => setPasswordForm((prev) => ({ ...prev, currentPassword: e.target.value }))} disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="newPassword">새 비밀번호 *</label>
                    <input type="password" id="newPassword" value={passwordForm.newPassword} onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPassword: e.target.value }))} disabled={loading} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="newPasswordConfirm">새 비밀번호 확인 *</label>
                    <input type="password" id="newPasswordConfirm" value={passwordForm.newPasswordConfirm} onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPasswordConfirm: e.target.value }))} disabled={loading} />
                  </div>
                  <button type="submit" className="submit-button" disabled={loading}>{loading ? '변경 중...' : '비밀번호 변경'}</button>
                </form>
              </section>
            )}

            {activeTab === 'investment' && (
              <section>
                <h2>투자정보 설정</h2>
                {investmentError && <div className="error-message">{investmentError}</div>}
                <form className="profile-form" onSubmit={handleInvestmentSubmit}>
                  <div className="form-group">
                    <label htmlFor="investmentAmount">투자금 (단위: 만원)</label>
                    <input
                      id="investmentAmount"
                      type="number"
                      min="0"
                      value={investmentForm.investmentAmount}
                      onChange={(e) => setInvestmentForm((prev) => ({ ...prev, investmentAmount: e.target.value }))}
                      disabled={investmentLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="houseCount">주택수</label>
                    <input
                      id="houseCount"
                      type="number"
                      min="0"
                      value={investmentForm.houseCount}
                      onChange={(e) => {
                        const nextHouseCount = Number(e.target.value || 0);
                        setInvestmentForm((prev) => ({
                          ...prev,
                          houseCount: e.target.value,
                          isFirstHomeBuyer: nextHouseCount === 1 ? prev.isFirstHomeBuyer : false,
                        }));
                      }}
                      disabled={investmentLoading}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="isFirstHomeBuyer">생애최초 구입 여부 (1주택자만)</label>
                    <select
                      id="isFirstHomeBuyer"
                      value={investmentForm.isFirstHomeBuyer ? 'Y' : 'N'}
                      onChange={(e) => setInvestmentForm((prev) => ({ ...prev, isFirstHomeBuyer: e.target.value === 'Y' }))}
                      disabled={investmentLoading || Number(investmentForm.houseCount || 0) !== 1}
                    >
                      <option value="N">아니오</option>
                      <option value="Y">예</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="applyYn">적용 여부</label>
                    <select
                      id="applyYn"
                      value={investmentForm.apply ? 'Y' : 'N'}
                      onChange={(e) => setInvestmentForm((prev) => ({ ...prev, apply: e.target.value === 'Y' }))}
                      disabled={investmentLoading}
                    >
                      <option value="Y">적용</option>
                      <option value="N">미적용</option>
                    </select>
                  </div>
                  <button type="submit" className="submit-button" disabled={investmentLoading}>
                    {investmentLoading ? '저장 중...' : '투자정보 저장'}
                  </button>
                </form>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyPage;
